import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Brain, TrendingUp, Target, CheckCircle, Clock, AlertTriangle, RefreshCw } from 'lucide-react';

const PatternLearningDashboard = ({ calculationId, clientId = 'current_client' }) => {
  const [patterns, setPatterns] = useState([]);
  const [stats, setStats] = useState({});
  const [pendingRules, setPendingRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API = (process.env.REACT_APP_BACKEND_URL || "") || 'http://localhost:8001';

  useEffect(() => {
    loadPatternAnalytics();
    loadPendingRules();
  }, [clientId]);

  const loadPatternAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/client-patterns/analytics/${clientId}`);
      
      if (response.ok) {
        const data = await response.json();
        setPatterns(data.patterns || []);
        setStats(data.stats || {});
      } else if (response.status === 404) {
        // No patterns yet - this is normal for new clients
        setPatterns([]);
        setStats({});
      } else {
        throw new Error('Failed to load pattern analytics');
      }
    } catch (err) {
      console.error('Error loading pattern analytics:', err);
      setError('Failed to load pattern analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRules = async () => {
    try {
      const response = await fetch(`${API}/api/client-patterns/pending-rules/${clientId}`);
      
      if (response.ok) {
        const data = await response.json();
        setPendingRules(data.pending_rules || []);
      }
    } catch (err) {
      console.error('Error loading pending rules:', err);
    }
  };

  const approveRule = async (ruleId) => {
    try {
      const response = await fetch(`${API}/api/client-patterns/approve-rule/${ruleId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: 'current_user' })
      });

      if (response.ok) {
        setPendingRules(prev => prev.filter(rule => rule.id !== ruleId));
        await loadPatternAnalytics(); // Refresh stats
      } else {
        throw new Error('Failed to approve rule');
      }
    } catch (err) {
      console.error('Error approving rule:', err);
      setError('Failed to approve rule');
    }
  };

  const rejectRule = async (ruleId) => {
    try {
      const response = await fetch(`${API}/api/client-patterns/reject-rule/${ruleId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejected_by: 'current_user' })
      });

      if (response.ok) {
        setPendingRules(prev => prev.filter(rule => rule.id !== ruleId));
      } else {
        throw new Error('Failed to reject rule');
      }
    } catch (err) {
      console.error('Error rejecting rule:', err);
      setError('Failed to reject rule');
    }
  };

  const refreshData = async () => {
    await Promise.all([loadPatternAnalytics(), loadPendingRules()]);
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 90) return 'text-green-600';
    if (accuracy >= 75) return 'text-blue-600';
    if (accuracy >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAutomationColor = (rate) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-blue-600';
    if (rate >= 40) return 'text-yellow-600';
    return 'text-orange-600';
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-indigo-500" />
              <h3 className="text-lg font-semibold">AI Learning Progress</h3>
            </div>
            <Button variant="outline" size="sm" onClick={refreshData} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          <p className="text-sm text-gray-600">
            Monitor how AI learns from your mapping decisions and improves over time
          </p>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Learning Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-indigo-600">
                {stats.patterns_learned || 0}
              </div>
              <div className="text-sm text-gray-600">Patterns Learned</div>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
              <div className={`text-2xl font-bold ${getAccuracyColor(stats.accuracy || 0)}`}>
                {stats.accuracy || 0}%
              </div>
              <div className="text-sm text-gray-600">AI Accuracy</div>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg">
              <div className={`text-2xl font-bold ${getAutomationColor(stats.automation_rate || 0)}`}>
                {stats.automation_rate || 0}%
              </div>
              <div className="text-sm text-gray-600">Automation Rate</div>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-pink-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {stats.corrections_processed || 0}
              </div>
              <div className="text-sm text-gray-600">Corrections Processed</div>
            </div>
          </div>

          {/* Pending Rules Section */}
          {pendingRules.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-orange-500" />
                <h4 className="font-medium">Pending Rule Approvals</h4>
                <Badge variant="outline">{pendingRules.length}</Badge>
              </div>
              
              <div className="space-y-2">
                {pendingRules.map((rule) => (
                  <Card key={rule.id} className="bg-orange-50 border-orange-200">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 mb-1">
                            {rule.rule_name || 'Auto-Generated Rule'}
                          </div>
                          <div className="text-sm text-gray-600 mb-2">
                            <strong>Pattern:</strong> {rule.pattern_description}
                          </div>
                          <div className="text-sm text-gray-600 mb-2">
                            <strong>Based on:</strong> {rule.correction_count} corrections
                          </div>
                          <div className="text-xs text-gray-500">
                            Created: {new Date(rule.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            onClick={() => approveRule(rule.id)}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rejectRule(rule.id)}
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Learned Patterns */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-green-500" />
              <h4 className="font-medium">Recent Patterns Detected</h4>
            </div>
            
            {patterns.length > 0 ? (
              <div className="space-y-2">
                {patterns.slice(0, 5).map((pattern, index) => (
                  <Card key={index} className="bg-gray-50">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-gray-900 mb-1">
                            {pattern.pattern_name}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            Detected from {pattern.correction_count} corrections
                          </div>
                          <div className="text-xs text-gray-500">
                            Confidence: {(pattern.confidence * 100).toFixed(0)}%
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Badge variant={pattern.is_active ? 'default' : 'secondary'}>
                            {pattern.is_active ? 'Active' : 'Learning'}
                          </Badge>
                          {pattern.accuracy && (
                            <Badge variant="outline" className="text-xs">
                              {pattern.accuracy}% accurate
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {pattern.rule_preview && (
                        <div className="mt-2 p-2 bg-white rounded border text-xs">
                          <strong>Rule:</strong> {pattern.rule_preview}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Brain className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-600 mb-1">No patterns detected yet</p>
                <p className="text-sm text-gray-500">
                  Start mapping accounts and correcting AI suggestions to help the system learn your patterns
                </p>
              </div>
            )}
          </div>

          {/* Learning Progress Indicator */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-blue-800">Learning Status</span>
            </div>
            <div className="text-sm text-blue-700">
              {stats.patterns_learned === 0 ? (
                "🌱 AI is ready to learn from your first mapping decisions"
              ) : stats.accuracy >= 90 ? (
                "🎯 AI has excellent accuracy - providing high-quality suggestions"
              ) : stats.accuracy >= 75 ? (
                "📈 AI is learning well - accuracy improving with each correction"
              ) : stats.patterns_learned >= 3 ? (
                "🔄 AI is building understanding - more corrections will improve accuracy"
              ) : (
                "📊 AI needs more examples to detect reliable patterns"
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatternLearningDashboard;
